/**
 * useOnlineGame — thin wrapper for online multiplayer sync via Firebase.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { listenMatch, playCard as playCardService, startNextRound as startNextRoundService, MatchData } from '../services/matchmaking';
import { Card } from '../types';
import { getPossibleCaptures } from '../lib/gameUtils';
import { db } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import {
  emitLifecycleEventOnce,
  onlineGameStartEvent,
  roundCompleteEvent,
} from '../lib/productAnalytics';
import { recordOperationalMetricOnce } from '../lib/operationalMetrics';

const DISCONNECT_TIMEOUT_MS = 60_000; // 60 seconds

interface UseOnlineGameResult {
  match: MatchData | null;
  loading: boolean;
  error: string | null;
  isMyTurn: boolean;
  myPlayerIndex: number;
  playCard: (card: Card) => Promise<void>;
  selectCaptureOption: (capturedCards: Card[]) => Promise<void>;
  possibleCaptures: Card[][];
  disconnect: () => void;
  opponentDisconnected: boolean;
  clearDisconnect: () => void;
  isConnected: boolean;
  startNextRound: () => Promise<void>;
  cancelSelection: () => void;
}

export function useOnlineGame(matchId: string, myUid: string): UseOnlineGameResult {
  const [match, setMatch] = useState<MatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [possibleCaptures, setPossibleCaptures] = useState<Card[][]>([]);
  const [opponentDisconnected, setOpponentDisconnected] = useState(false);
  const [isConnected, setIsConnected] = useState(true);

  const selectedCardRef = useRef<Card | null>(null);
  const matchRef = useRef<MatchData | null>(null);
  const lastUpdateRef = useRef<number>(Date.now());
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousStatusRef = useRef<MatchData['status'] | null>(null);
  const roundStartTotalsRef = useRef<number[]>([]);

  useEffect(() => {
    matchRef.current = match;
  }, [match]);

  // Find my player index dynamically
  const myPlayerIndex = match ? match.players.findIndex(p => p.uid === myUid) : -1;
  const isMyTurn = myPlayerIndex !== -1 && match?.currentPlayerIndex === myPlayerIndex && match?.status === 'playing';

  const resetDisconnectTimer = useCallback(() => {
    lastUpdateRef.current = Date.now();
    setOpponentDisconnected(false);
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
    }
    disconnectTimerRef.current = setTimeout(() => {
      setOpponentDisconnected(true);
    }, DISCONNECT_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    const connectedRef = ref(db, '.info/connected');
    const unsubscribe = onValue(connectedRef, (snapshot) => {
      const connected = snapshot.val() === true;
      setIsConnected(connected);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!matchId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    previousStatusRef.current = null;
    roundStartTotalsRef.current = [];
    resetDisconnectTimer();

    const unsubscribe = listenMatch(matchId, (matchData) => {
      const normalized = normalizeMatchData(matchData);
      setMatch(normalized);
      setLoading(false);
      setError(null);
      resetDisconnectTimer();
    }, (err) => {
      console.error('[useOnlineGame] listenMatch error:', err);
      setError(`Errore di connessione: ${err.message}`);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
      }
    };
  }, [matchId, myUid, resetDisconnectTimer]);

  // Observe authoritative Firebase status transitions. Lifecycle events use a
  // tab-session dedupe key and never include the private match code in payloads.
  useEffect(() => {
    if (!match || myPlayerIndex === -1) return;

    const previousStatus = previousStatusRef.current;
    const observedAt = Date.now();
    const currentTotals = match.players.map(player => player.totalScore ?? 0);

    if (match.status === 'playing') {
      if (previousStatus !== 'playing') {
        if (previousStatus === null || previousStatus === 'waiting') {
          recordOperationalMetricOnce('online_participation_start', `online-start:${matchId}:${myUid}`);
          emitLifecycleEventOnce(onlineGameStartEvent({
            matchId,
            players: match.numPlayers,
            targetScore: match.targetScore,
            createdAt: match.createdAt,
            observedAt,
          }));
        }
        roundStartTotalsRef.current = currentTotals;
      }
    } else if (
      (match.status === 'roundEnd' || match.status === 'finished')
      && (previousStatus === 'playing' || previousStatus === null)
    ) {
      const baseline = roundStartTotalsRef.current[myPlayerIndex];
      const totalScore = currentTotals[myPlayerIndex];
      const roundScore = baseline === undefined ? undefined : totalScore - baseline;
      const completedAt = match.roundEndedAt ?? observedAt;
      const roundIdentity = match.roundEndedAt === undefined
        ? `${matchId}:final`
        : `${matchId}:${match.roundEndedAt}`;

      emitLifecycleEventOnce(roundCompleteEvent({
        mode: 'online',
        roundKey: roundIdentity,
        players: match.numPlayers,
        roundScore,
        totalScore,
        completedAt,
      }));
    }

    previousStatusRef.current = match.status;
  }, [match?.status, match?.roundEndedAt, matchId, myPlayerIndex]);

  useEffect(() => {
    if (!selectedCardRef.current || !match) {
      setPossibleCaptures([]);
      return;
    }
    const captures = getPossibleCaptures(selectedCardRef.current, match.table);
    setPossibleCaptures(captures);
  }, [match?.table, match?.currentPlayerIndex]);

  const playCard = useCallback(async (card: Card) => {
    const currentMatch = matchRef.current;
    if (!currentMatch) return;
    const myIdx = currentMatch.players.findIndex(p => p.uid === myUid);
    if (myIdx === -1) return;
    const myTurn = currentMatch.currentPlayerIndex === myIdx && currentMatch.status === 'playing';
    if (!myTurn) return;

    selectedCardRef.current = card;
    const captures = getPossibleCaptures(card, currentMatch.table ?? []);

    if (captures.length === 0) {
      await playCardService(matchId, myIdx, card, null, myUid);
      selectedCardRef.current = null;
      setPossibleCaptures([]);
    } else if (captures.length === 1) {
      await playCardService(matchId, myIdx, card, captures[0], myUid);
      selectedCardRef.current = null;
      setPossibleCaptures([]);
    } else {
      setPossibleCaptures(captures);
    }
  }, [matchId, myUid]);

  const selectCaptureOption = useCallback(async (capturedCards: Card[]) => {
    if (!selectedCardRef.current) return;
    const currentMatch = matchRef.current;
    if (!currentMatch) return;
    const myIdx = currentMatch.players.findIndex(p => p.uid === myUid);
    if (myIdx === -1) return;
    await playCardService(matchId, myIdx, selectedCardRef.current, capturedCards, myUid);
    selectedCardRef.current = null;
    setPossibleCaptures([]);
  }, [matchId, myUid]);

  const disconnect = useCallback(() => {
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
    }
    setMatch(null);
  }, []);

  const clearDisconnect = useCallback(() => {
    setOpponentDisconnected(false);
    resetDisconnectTimer();
  }, [resetDisconnectTimer]);

  const startNextRound = useCallback(async () => {
    if (!match || match.status !== 'roundEnd') return;
    await startNextRoundService(matchId, myUid);
  }, [match, matchId, myUid]);

  const cancelSelection = useCallback(() => {
    selectedCardRef.current = null;
    setPossibleCaptures([]);
  }, []);

  // Auto-clear selection when it is no longer my turn
  useEffect(() => {
    if (!isMyTurn) {
      selectedCardRef.current = null;
      setPossibleCaptures([]);
    }
  }, [isMyTurn]);

  return {
    match,
    loading,
    error,
    isMyTurn,
    myPlayerIndex,
    playCard,
    selectCaptureOption,
    possibleCaptures,
    disconnect,
    opponentDisconnected,
    clearDisconnect,
    isConnected,
    startNextRound,
    cancelSelection,
  };
}

function normalizeMatchData(data: MatchData | null): MatchData | null {
  if (!data) return null;

  const normalized = { ...data };
  if (!normalized.table) {
    normalized.table = [];
  }
  if (!normalized.deck) {
    normalized.deck = [];
  }

  const numPlayers = normalized.numPlayers || 2;

  if (!normalized.players) {
    normalized.players = Array(numPlayers).fill(null).map(() => ({
      uid: '',
      name: '',
      hand: [],
      captured: [],
      totalScore: 0,
      hasDeclaredLowHand: false,
      hasDeclaredThreeAces: false,
      lowHandCount: 0,
      threeAcesCount: 0,
      readyForNextRound: false,
    }));
  } else {
    normalized.players = normalized.players.map((p: any) => {
      if (!p) return {
        uid: '',
        name: '',
        hand: [],
        captured: [],
        totalScore: 0,
        hasDeclaredLowHand: false,
        hasDeclaredThreeAces: false,
        lowHandCount: 0,
        threeAcesCount: 0,
        readyForNextRound: false,
      };
      return {
        uid: p.uid || '',
        name: p.name || '',
        hand: p.hand || [],
        captured: p.captured || [],
        totalScore: p.totalScore ?? 0,
        hasDeclaredLowHand: p.hasDeclaredLowHand ?? false,
        hasDeclaredThreeAces: p.hasDeclaredThreeAces ?? false,
        lowHandCount: p.lowHandCount ?? 0,
        threeAcesCount: p.threeAcesCount ?? 0,
        readyForNextRound: p.readyForNextRound ?? false,
        disconnected: p.disconnected ?? false,
      };
    });
  }

  if (!normalized.scope) {
    normalized.scope = Array(numPlayers).fill(0);
  } else {
    normalized.scope = Array.from({ length: numPlayers }, (_, i) => normalized.scope[i] ?? 0);
  }

  if (!normalized.revealedHands) {
    normalized.revealedHands = Array(numPlayers).fill([]).map(() => []);
  } else {
    normalized.revealedHands = Array.from({ length: numPlayers }, (_, i) => normalized.revealedHands[i] || []);
  }

  return normalized;
}
